import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const STORAGE_KEY = 'perron:recent-searches';
const MAX_RECENT = 6;

/**
 * Wrapper fino sobre localStorage para búsquedas recientes. SSR-safe: en
 * servidor devuelve siempre `[]` y los writes no-op. Mantiene orden (más
 * reciente primero) y dedupea case-insensitive.
 */
@Injectable({ providedIn: 'root' })
export class RecentSearchesStore {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly canUseStorage = isPlatformBrowser(this.platformId);

  load(): string[] {
    if (!this.canUseStorage) return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((x): x is string => typeof x === 'string').slice(0, MAX_RECENT);
    } catch {
      return [];
    }
  }

  push(term: string): string[] {
    const normalized = term.trim();
    if (!normalized) return this.load();
    const current = this.load();
    const next = [normalized, ...current.filter((t) => t.toLowerCase() !== normalized.toLowerCase())].slice(0, MAX_RECENT);
    this.persist(next);
    return next;
  }

  remove(term: string): string[] {
    const current = this.load();
    const next = current.filter((t) => t.toLowerCase() !== term.toLowerCase());
    this.persist(next);
    return next;
  }

  clear(): string[] {
    this.persist([]);
    return [];
  }

  private persist(list: string[]): void {
    if (!this.canUseStorage) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // Quota / privacy mode — silenciar, no rompe la UX.
    }
  }
}
